import React, { forwardRef } from 'react';
import { styled } from '@mentimeter/ragnar-styled';
import { clsx, cn } from '@mentimeter/ragnar-tailwind-config';
import { inputVariants, type InputVariantProps } from '../utils';
import { Box } from '../../box';
import { SelectItemIcon } from './SelectItemIcon';

type ValueT = string | number;

export interface OptionT
  extends React.DetailedHTMLProps<
    React.OptionHTMLAttributes<HTMLOptionElement>,
    HTMLOptionElement
  > {
  children: string;
  value?: ValueT | undefined;
  disabled?: boolean | undefined;
}

const SelectItemStyled = styled('select', {
  displayName: 'Select',
})('kosmosSpacing', 'width');

interface SelectItemProps
  extends Omit<
    React.ComponentProps<typeof SelectItemStyled>,
    'p' | 'py' | 'px' | 'pt' | 'pb' | 'pr' | 'pl'
  > {
  inputSize?: InputVariantProps['size'];
  status?: InputVariantProps['status'];
  variant?: InputVariantProps['variant'];
  disabled?: boolean | undefined;
}

const SelectItem = forwardRef<HTMLElement | null, SelectItemProps>(
  (
    {
      inputSize,
      variant,
      status,
      className,
      disabled,
      width,
      m,
      mx,
      my,
      mt,
      ml,
      mr,
      mb,
      extend,
      ...props
    },
    ref,
  ) => {
    const optionClasses = [
      '[&>option]:bg-bg',
      '[&>option]:text-text',
      '[&>option]:font-sans',
      '[&>optgroup]:bg-bg',
      '[&>optgroup]:text-text',
      '[&>optgroup]:font-sans',
    ];

    const inputVariantClasses = inputVariants({
      size: inputSize,
      status,
      variant,
    });

    const overrideClasses = [
      'cursor-pointer',
      inputSize === 'compact' ? 'pr-6' : 'pr-8',
    ];

    const marginProps = { m, mx, my, mt, ml, mr, mb };

    return (
      <Box
        width={width}
        extend={extend}
        className={clsx('relative', 'w-full', className)}
        {...marginProps}
      >
        <SelectItemStyled
          ref={ref}
          className={cn(
            'r-select',
            optionClasses,
            inputVariantClasses,
            overrideClasses,
          )}
          disabled={disabled}
          {...props}
        />
        <SelectItemIcon size={inputSize} disabled={disabled} variant={status} />
      </Box>
    );
  },
);

type SelectType = typeof SelectItem;

interface SelectI extends SelectType {
  Option: React.FC<OptionT>;
}

(SelectItem as SelectI).Option = (props) => <option {...props} />;

export type SelectItemT = React.ComponentProps<SelectI>;
/**
 * @deprecated Use composable Select components from the ragnar-ui/input/select directory instead. For documentation, see https://storybook.mentimeter.org/?path=/docs/global-system-ragnar-ui-select--docs
 */
export default SelectItem as SelectI;
